// Call notes (D-111): what owners and admins found wrong in the AI's reading of a call when they ran it again. The log
// shows where readings go wrong, in the transcript or the analysis, and what the AI said before and says now.
import { fmtDateTime } from "./format";
import type { CallNote, NoteLayer, NoteReading } from "./types";

export const layerLabel: Record<NoteLayer, string> = {
  transcript: "Transcript",
  analysis: "Analysis",
  both: "Transcript and analysis",
};

/** What each choice in Run again does, said in the dialog. */
export const layerEffect: Record<NoteLayer, string> = {
  transcript: "The words or who said them were wrong. The call is transcribed and analysed again.",
  analysis: "The words were right; what the AI made of them was wrong. The transcript is kept and only the analysis " +
    "is made again.",
  both: "Both were wrong. The call is transcribed and analysed again.",
};

/** How many notes are about the transcript, the analysis and both. */
export function noteCounts(notes: CallNote[]): Record<NoteLayer, number> & { all: number } {
  const count = (layer: NoteLayer) => notes.filter((n) => n.layer === layer).length;
  return { all: notes.length, transcript: count("transcript"), analysis: count("analysis"), both: count("both") };
}

/** What a reading came to, in one line: its follow-up and when, and when the customer asked for it. */
export function readingLine(r: NoteReading | null | undefined, tz: string): string {
  if (!r || Object.keys(r).length === 0) return "No analysis";
  const action = r.next_action?.type && r.next_action.type !== "none" ? r.next_action.type.replaceAll("_", " ") : null;
  const due = r.follow_up?.due_at ? fmtDateTime(r.follow_up.due_at, tz) : null;
  const asked = r.next_action?.time_hint?.text ?? r.next_action?.customer_said ?? null;
  const parts = [
    due ? `Follow-up ${due}` : action ? `Next: ${action}` : "No follow-up",
    asked ? `asked for "${asked}"` : null,
  ];
  return parts.filter(Boolean).join(", ");
}
